import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Lock, AlertCircle } from 'lucide-react';

const PasswordGate = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  const CORRECT_PASSWORD = 'Sovereign26';
  const AUTH_KEY = 'sovereign_investor_auth';

  useEffect(() => {
    // Check if already authenticated
    const authStatus = localStorage.getItem(AUTH_KEY);
    if (authStatus === 'true') {
      setIsAuthenticated(true);
    }
    setIsLoading(false);
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');

    if (password === CORRECT_PASSWORD) {
      localStorage.setItem(AUTH_KEY, 'true');
      setIsAuthenticated(true);
    } else {
      setError('Invalid access code. Please contact Sovereign for access.');
      setPassword('');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse">
          <p className="text-2xl font-semibold">SOVEREIGN</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-secondary/20 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="w-full max-w-md"
        >
          <div className="text-center mb-8">
            <h1 className="text-3xl font-semibold mb-2">SOVEREIGN</h1>
            <p className="text-sm text-muted-foreground">Private Investor Room</p>
          </div>

          <Card className="p-8 border-accent/20">
            <div className="flex items-center justify-center mb-6">
              <div className="bg-accent/10 p-4 rounded-full">
                <Lock className="h-8 w-8 text-accent" />
              </div>
            </div>

            <h2 className="text-xl font-semibold text-center mb-2">Access Required</h2>
            <p className="text-sm text-muted-foreground text-center mb-6">
              This investor presentation is password-protected. Please enter your access code to continue.
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="password">Access Code</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter access code"
                  className="mt-2"
                  autoFocus
                  required
                />
              </div>

              <AnimatePresence>
                {error && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="flex items-start gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-md"
                  >
                    <AlertCircle className="h-4 w-4 text-destructive flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-destructive">{error}</p>
                  </motion.div>
                )}
              </AnimatePresence>

              <Button type="submit" className="w-full" size="lg">
                Enter Investor Room
              </Button>
            </form>

            <div className="mt-6 pt-6 border-t text-center">
              <p className="text-xs text-muted-foreground">
                Need access? Contact{' '}
                <a href="mailto:info@sovereigngroup.io" className="text-accent hover:underline">
                  info@sovereigngroup.io
                </a>
              </p>
            </div>
          </Card>

          <p className="text-xs text-muted-foreground text-center mt-6">
            Confidential — For intended recipients only
          </p>
        </motion.div>
      </div>
    );
  }

  return <>{children}</>;
};

export default PasswordGate;
